import { useMemo } from 'react';

import {
  CREATE_TIME_FILTER,
  DemoDataSetupBanner,
  SearchUtils,
  UPDATE_TIME_FILTER,
  createFilter,
} from '@spotify/confidence-core-react';
import { useAccountMode } from '@spotify/confidence-graphql';
import {
  FlagFilterEditor,
  FlagFilterViewer,
} from '@spotify/plugin-confidence-flags';
import {
  CREATOR_FILTER,
  OWNER_FILTER,
} from '@spotify/plugin-confidence-iam-react';
import { SURFACE_FILTER } from '@spotify/plugin-confidence-surfaces-react';
import { experimentsRouteRef } from '@spotify/plugin-confidence-workflows-react';
import {
  ACTUAL_END_TIME_FILTER,
  ACTUAL_START_TIME_FILTER,
  ENTITY_FILTER,
  ExperimentsPageLayout,
  type ExperimentsPageLayoutProps,
  OUTCOME_FILTER,
  PLANNED_END_TIME_FILTER,
  PLANNED_START_TIME_FILTER,
  STATE_FILTER,
  TYPE_FILTER,
  WorkflowInstanceTable,
  createTypeFilter,
} from '@spotify/plugin-confidence-workflows-react';

const REANALYSIS_WORKFLOW_ID = 'reanalysis';
const ANALYSIS_WORKFLOW_ID = 'analysis';

const TYPE_FILTER_WITHOUT_ANALYSIS = createTypeFilter({
  excludeWorkflowIds: [REANALYSIS_WORKFLOW_ID, ANALYSIS_WORKFLOW_ID],
});

const FLAG_FILTER = createFilter<string[]>({
  id: 'flags',
  label: 'Flag',
  hotkey: 'f',
  query: (value: string[]) =>
    value
      .map(SearchUtils.escapeResourceName)
      .map(
        flag =>
          `module_data.abtest.flag:${flag} OR module_data.rollout.variant:${flag}*`,
      )
      .join(' OR '),
  viewer: FlagFilterViewer,
  editor: FlagFilterEditor,
});

const SHARED_FILTERS = [
  OWNER_FILTER,
  CREATOR_FILTER,
  CREATE_TIME_FILTER,
  UPDATE_TIME_FILTER,
  STATE_FILTER,
  OUTCOME_FILTER,
  FLAG_FILTER,
  SURFACE_FILTER,
  ENTITY_FILTER,
  PLANNED_START_TIME_FILTER,
  PLANNED_END_TIME_FILTER,
  ACTUAL_START_TIME_FILTER,
  ACTUAL_END_TIME_FILTER,
];

const FILTERS = [TYPE_FILTER, ...SHARED_FILTERS];

const FILTERS_TOTAL_CONFIDENCE = [
  TYPE_FILTER_WITHOUT_ANALYSIS,
  ...SHARED_FILTERS,
];

const ExperimentsPageComponent = () => {
  const { isTotalConfidence } = useAccountMode();

  const filters = isTotalConfidence ? FILTERS_TOTAL_CONFIDENCE : FILTERS;
  const tableFilter = isTotalConfidence
    ? `-(parent:"workflows/${REANALYSIS_WORKFLOW_ID}")`
    : undefined;

  const filterExtensions: ExperimentsPageLayoutProps['filterExtensions'] =
    useMemo(
      () =>
        isTotalConfidence
          ? exts =>
              exts.filter(ext => ext.workflowId !== REANALYSIS_WORKFLOW_ID)
          : undefined,
      [isTotalConfidence],
    );

  return (
    <ExperimentsPageLayout
      routeRef={experimentsRouteRef}
      storageKey="confidence-experiments"
      filters={filters}
      filterExtensions={filterExtensions}
      renderTable={({ mode }) => (
        <>
          <DemoDataSetupBanner />
          <WorkflowInstanceTable
            mode={mode}
            filter={tableFilter}
            emptyStateProps={{
              type: 'experiment',
              typePlural: 'experiments',
              description: (
                <>
                  Experiments include A/B tests and rollouts across your
                  workspace.
                </>
              ),
            }}
          />
        </>
      )}
    />
  );
};

export const ExperimentsPage = ExperimentsPageComponent;
